import { InputComponentProps } from '@/components/input'
import { INPUT_PHONE_KEY, INPUT_PHONE_STORAGE } from '@/components/input-phone-storage'
import { useAppContext } from '@/pages/_app'
import { useVenueContext } from '@/services/venue'
import {
  useDefaultObservable,
  useEffect$,
  uiEvents,
  useSubscription,
  filterEvents,
  useSubject,
  useObservableState,
  makeEmmiterFor,
} from '@/utils/event'
import { parsePhoneNumberFromString, getExampleNumber } from 'libphonenumber-js/max'
import { CountryCode, NationalNumber } from 'libphonenumber-js'
import { useRef, useEffect } from 'react'
import { filter, map, mapTo, merge, skip } from 'rxjs'
import { useObservable } from 'rxjs-hooks'

export type InputParams = {
  id: string
  startingValue: string
  storage: Storage

  disabled: boolean
  required: boolean
  validate?: (value: string) => string[]
  validateOn?: 'change' | 'blur'
  transform?: (value: string) => string | null

  type: string
  label: string
  helperText?: string | JSX.Element
  autoFocus?: boolean
  rows?: number
}

export function useInput(params: InputParams) {
  const { id, disabled, required, label, rows } = params
  const { t } = useAppContext()
  const validateOn = params.validateOn ?? 'change'

  const elementRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const inputValueRef = useRef(params.startingValue)

  const change$ = useSubject<{ target: { value: string } }>()

  const value$ = useObservableState({
    id: `input/v2/${id}`,
    source: change$,
    storage: params.storage,
    startingValue: params.startingValue,
    scan: (_oldValue, event) => {
      const newValue = event.target.value
      if (!params.transform) {
        return newValue
      }

      return params.transform(newValue) ?? newValue
    },
  })

  const inputValue = useDefaultObservable(value$)

  // we need the ref for accesing the latest state in closures (stale closure problem)
  // eslint-disable-next-line immutable/no-mutation
  inputValueRef.current = inputValue

  const focus$ = useSubject<void>()
  const blur$ = useSubject<void>()
  const touch$ = useSubject<void>()

  const focused = useObservable(() => {
    return merge(focus$.pipe(mapTo(true)), blur$.pipe(mapTo(false)))
  }, false)

  const touched = useObservable(() => {
    return merge(value$.pipe(skip(1)), blur$, touch$).pipe(mapTo(true))
  }, false)

  const onLoad$ = useEffect$([])

  const validateInput = (inputValue: string) => {
    return params.validate ? params.validate(inputValue) : []
  }

  const validationErrors$ = merge(
    value$.pipe(
      filter(() => {
        return validateOn === 'change'
      }),
    ),
    merge(onLoad$, blur$, touch$).pipe(
      map(() => {
        return inputValueRef.current
      }),
      filter((value) => {
        return value.length > 0
      }),
    ),
  ).pipe(
    map((value) => {
      return validateInput(value)
    }),
  )
  const validationErrors = useObservable(() => {
    return validationErrors$
  }, [])

  const helperText = (() => {
    if (validationErrors.length > 0) {
      return validationErrors.join(', ')
    }

    if (params.helperText && (!params.required || !touched || inputValue !== '')) {
      return params.helperText
    }

    if (params.required) {
      return t('common:required')
    }

    return undefined
  })()

  const isValid = (validationErrors: string[]) => {
    return (!params.required || inputValue.trim().length > 0) && validationErrors.length === 0
  }

  const valid = !touched || isValid(validationErrors)

  useSubscription(uiEvents.pipe(filterEvents(['AppLoaded'])), () => {
    if (params.autoFocus) {
      elementRef.current?.focus()
    }
  })

  const componentProps: InputComponentProps = {
    id,
    type: params.type,
    inputRef: elementRef,
    value: inputValueRef,
    focused,
    touched,
    valid,
    validationErrors,
    disabled,
    required,
    helperText,
    label,
    rows,
    actions: {
      change: makeEmmiterFor(change$),
      focus: makeEmmiterFor(focus$),
      blur: makeEmmiterFor(blur$),
      touch: () => {
        touch$.next()
        return {
          isValid: isValid(validateInput(inputValueRef.current)),
        }
      },
    },
  }

  return componentProps
}

export type PhoneInputParams = {
  required: boolean
  label: string
}

export function usePhoneInput(props: PhoneInputParams) {
  const { t } = useAppContext()
  const { venue } = useVenueContext()!

  const venueCountryCode = venue.country.toUpperCase() as CountryCode
  const getCountryCodeByInput = (input: string) => {
    if (input.startsWith('+')) {
      return undefined
    }

    return venueCountryCode
  }

  const phoneExamples: { [key in CountryCode]: NationalNumber } = {
    ...require('libphonenumber-js/examples.mobile.json'),
    GB: '2012345678',
    RU: '9261234567',
  }

  const input = useInput({
    ...props,
    id: INPUT_PHONE_KEY,
    storage: INPUT_PHONE_STORAGE,
    type: 'tel',
    startingValue: '',
    disabled: false,
    validateOn: 'blur',
    validate: (input) => {
      const countryCode = getCountryCodeByInput(input)
      const phoneNumber = parsePhoneNumberFromString(input, countryCode)
      const numberIsValid =
        phoneNumber &&
        phoneNumber.isValid() &&
        (!countryCode || phoneNumber.country === countryCode)

      if (input.length > 0 && !numberIsValid) {
        const example = countryCode ? getExampleNumber(countryCode, phoneExamples)! : null

        return [
          example
            ? t('inputPhone:correctFormat', {
                example: example.formatNational().replace(/[^\d]/g, ''),
              })
            : t('inputPhone:invalidFormat'),
        ]
      }

      return []
    },
  })

  useEffect(() => {
    const inputValue = input.value.current
    if (inputValue && inputValue.length > 0) {
      input.actions.touch()
    }
  }, [])

  return input
}
